import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
  Button,
  keyframes,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingDown as NegativeIcon,
  TrendingUp as PositiveIcon,
  Remove as NeutralIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
  WarningAmber as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Star as StarIcon,
  Person as PersonIcon,
  AutoAwesome as SparkleIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  SentimentSatisfiedAlt as SentimentIcon,
} from '@mui/icons-material';
import { Title, useDataProvider, useGetList, useNotify, usePermissions } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getChainId } from '../../../lib';
import { motion, AnimatePresence } from 'framer-motion';
import { PERMISSIONS } from '../../../constants';
import ManualLinkButton from '../../../manual/components/ManualLinkButton';
import { track, EVENTS } from '../../../lib/analytics';
import InsightsBar from './InsightsBar';
import DualScoreCard from './DualScoreCard';
import StationDivergenceTable from './StationDivergenceTable';
import PollItemsHighlights from './PollItemsHighlights';
import PollItemsTrend from './PollItemsTrend';
import AttendantHighlights from './AttendantHighlights';
import InferenceCard, { InferenceData } from './InferenceCard';
import {
  CustomerRatingsData,
  PollItemsData,
  AttendantsData,
  InsightsData,
} from './types';

// ─── Animations ───────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const sparkleFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
  50% { transform: translateY(-6px) rotate(180deg); opacity: 0.3; }
`;

const EASE_OUT = [0.25, 0.1, 0.25, 1] as [number, number, number, number];

const fadeSlideIn = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3, ease: EASE_OUT },
};

// ─── Enums / constants ────────────────────────────────────────────────────────

enum SentimentFilter {
  all = 'all',
  negative = 'negative',
  positive = 'positive',
  neutral = 'neutral',
}

const SENTIMENT_COLORS: Record<SentimentFilter | string, string> = {
  [SentimentFilter.negative]: '#ef4444',
  [SentimentFilter.positive]: '#22c55e',
  [SentimentFilter.neutral]: '#94a3b8',
};

const SENTIMENT_OPTIONS = [
  { value: SentimentFilter.negative, label: 'Negativo' },
  { value: SentimentFilter.positive, label: 'Positivo' },
  { value: SentimentFilter.neutral, label: 'Neutro' },
  { value: SentimentFilter.all, label: 'Todos' },
];

const SENTIMENT_LEGEND = [
  { color: SENTIMENT_COLORS[SentimentFilter.positive], label: 'Positivo', dataKey: 'positiveCount' },
  { color: SENTIMENT_COLORS[SentimentFilter.neutral], label: 'Neutro', dataKey: 'neutralCount' },
  { color: SENTIMENT_COLORS[SentimentFilter.negative], label: 'Negativo', dataKey: 'negativeCount' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface CategorySummaryItem {
  category: string;
  total: number;
  negativeCount: number;
  neutralCount: number;
  positiveCount: number;
}

interface StationCategory {
  category: string;
  total: number;
  negativeCount: number;
  neutralCount: number;
  positiveCount: number;
}

interface StationBreakdownItem {
  stationId: string;
  chainId: string;
  categories: StationCategory[];
  totalCount: number;
}

interface AttentionComment {
  id: string;
  comment: string;
  commentSentiment: number | null;
  commentCategories: string[];
  stationId: string;
  stationRating: number | null;
  attendantRating: number | null;
  createdAt: string;
  customerName: string | null;
}

interface SatisfactionScore {
  avgSentiment: number | null;
  totalComments: number;
  negativeCount: number;
  neutralCount: number;
  positiveCount: number;
}

interface AnalysisData {
  id: string;
  categorySummary: CategorySummaryItem[];
  stationBreakdown: StationBreakdownItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDateStr = (d: Date) => d.toISOString().slice(0, 10);

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toDateStr(d);
};

const defaultTo = () => toDateStr(new Date());

// ─── Filtros iniciais via query string (atalho "Ver comentários" do Painel 360) ─

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// `new Date('YYYY-MM-DD')` não rejeita dia inexistente (ex.: 2026-02-30 rola pra
// 2026-03-02) — confere se os componentes voltam iguais depois de montar a data.
const isValidDateStr = (value: string): boolean => {
  if (!DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

// `from`/`to` só viram estado inicial quando os DOIS são datas válidas em ordem —
// um dos dois ausente/inválido descarta o par inteiro e mantém o default de 30 dias
// (nunca aplica só um lado do intervalo).
const readInitialRange = (params: URLSearchParams): { from: string; to: string } | null => {
  const from = params.get('from');
  const to = params.get('to');
  if (!from || !to || !isValidDateStr(from) || !isValidDateStr(to) || from > to) return null;
  return { from, to };
};

const readInitialStationId = (params: URLSearchParams): string => {
  const stationId = params.get('stationId');
  return stationId && UUID_RE.test(stationId) ? stationId : '';
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard: React.FC<{ height?: number }> = ({ height = 200 }) => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height,
    }}
  />
);

// ─── Chart 1: Grouped Bar Chart (neg / neu / pos per category) ────────────────

const CategoryChart: React.FC<{ data: CategorySummaryItem[]; loading: boolean }> = ({
  data,
  loading,
}) => {
  if (loading) return <SkeletonCard height={380} />;
  if (!data.length)
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <AnalyticsIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
        <Typography color="text.secondary">Nenhum dado no período</Typography>
      </Card>
    );

  const chartHeight = Math.max(320, data.length * 52);

  return (
    <motion.div {...fadeSlideIn}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid rgba(120,105,223,0.10)', animation: `${pulseGlow} 4s ease-in-out infinite`, overflow: 'visible' }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #667eea, #7869DF)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 10px rgba(120,105,223,0.3)' }}>
              <BarChartIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
              O que seus clientes mais falam
            </Typography>
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {SENTIMENT_LEGEND.map(({ color, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
              </Box>
            ))}
          </Box>

          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data}
              layout="vertical"
              barSize={8}
              barGap={2}
              barCategoryGap="30%"
              margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis
                type="category"
                dataKey="category"
                width={170}
                tick={{ fontSize: 11, fill: '#475569' }}
                tickFormatter={(v: string) => v.length > 22 ? v.slice(0, 22) + '…' : v}
              />
              <Tooltip
                cursor={{ fill: 'rgba(102,126,234,0.05)' }}
                contentStyle={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  fontSize: 13,
                }}
              />
              {SENTIMENT_LEGEND.map(({ color, label, dataKey }) => (
                <Bar
                  key={dataKey}
                  dataKey={dataKey}
                  name={label}
                  fill={color}
                  radius={[0, 4, 4, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─── Chart 2: Canonical List for Selected Station ─────────────────────────────

interface Chart2Props {
  stationBreakdown: StationBreakdownItem[];
  selectedStationId: string;
  onSelectStation: (id: string) => void;
  loading: boolean;
}

const StationCanonicalList: React.FC<Chart2Props> = ({
  stationBreakdown,
  selectedStationId,
  onSelectStation,
  loading,
}) => {
  const [visibleCount, setVisibleCount] = useState(5);

  const { data: stationsData } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const stationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (stationsData ?? []).forEach((s: any) => map.set(s.id, s.name));
    return map;
  }, [stationsData]);

  const options = useMemo(() =>
    stationBreakdown.map(s => ({
      id: s.stationId,
      label: stationNameMap.get(s.stationId) ?? s.stationId,
      totalCount: s.totalCount,
    })),
    [stationBreakdown, stationNameMap]);

  const selectedOption = options.find(o => o.id === selectedStationId) ?? null;

  const canonicals = useMemo(() => {
    const station = stationBreakdown.find(s => s.stationId === selectedStationId);
    if (!station || !Array.isArray(station.categories)) return [] as StationCategory[];
    return [...station.categories].sort((a, b) => b.total - a.total);
  }, [selectedStationId, stationBreakdown]);

  const visible = canonicals.slice(0, visibleCount);
  const hasMore = visibleCount < canonicals.length;

  useEffect(() => setVisibleCount(5), [selectedStationId]);

  if (loading) return <SkeletonCard height={360} />;

  return (
    <motion.div {...fadeSlideIn}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid rgba(120,105,223,0.10)', animation: `${pulseGlow} 4s ease-in-out infinite` }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 10px rgba(14,165,233,0.3)' }}>
              <ShowChartIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
              Pulse de Sentimento por Posto
            </Typography>
          </Box>

          <Autocomplete
            size="small"
            options={options}
            value={selectedOption}
            onChange={(_, val) => onSelectStation(val?.id ?? '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText="Nenhum posto encontrado"
            renderInput={(params) => (
              <TextField {...params} label="Selecione o posto" variant="outlined" />
            )}
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography variant="body2" noWrap>{opt.label}</Typography>
                  <Chip
                    label={opt.totalCount}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#64748b', flexShrink: 0 }}
                  />
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />

          <AnimatePresence mode="wait">
            {!selectedStationId ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                  Selecione um posto para ver os canônicos
                </Typography>
              </Box>
            ) : canonicals.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                  Nenhum dado para este posto
                </Typography>
              </Box>
            ) : (
              <motion.div key={selectedStationId} {...fadeSlideIn}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {visible.map((item, idx) => {
                    const negPct = item.total > 0 ? (item.negativeCount / item.total) * 100 : 0;
                    const neuPct = item.total > 0 ? (item.neutralCount / item.total) * 100 : 0;
                    const posPct = item.total > 0 ? (item.positiveCount / item.total) * 100 : 0;
                    const delay = idx * 0.04;
                    return (
                      <motion.div
                        key={item.category}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay, duration: 0.25, ease: EASE_OUT }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1,
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#f8fafc' },
                            transition: 'background 0.15s',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: '#94a3b8', width: 20, textAlign: 'right', flexShrink: 0, fontSize: '0.75rem' }}
                          >
                            {idx + 1}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Name row */}
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5, fontSize: '0.875rem' }} noWrap>
                              {item.category}
                            </Typography>

                            {/* Split bar */}
                            <Box sx={{ height: 6, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden', display: 'flex' }}>
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${posPct}%` }}
                                transition={{ duration: 0.5, delay, ease: EASE_OUT }}
                                style={{ height: '100%', background: SENTIMENT_COLORS[SentimentFilter.positive] }}
                              />
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${neuPct}%` }}
                                transition={{ duration: 0.5, delay, ease: EASE_OUT }}
                                style={{ height: '100%', background: SENTIMENT_COLORS[SentimentFilter.neutral] }}
                              />
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${negPct}%` }}
                                transition={{ duration: 0.5, delay, ease: EASE_OUT }}
                                style={{ height: '100%', background: SENTIMENT_COLORS[SentimentFilter.negative] }}
                              />
                            </Box>

                            {/* Counters */}
                            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                              {item.positiveCount > 0 && (
                                <Typography variant="caption" sx={{ color: SENTIMENT_COLORS[SentimentFilter.positive], fontWeight: 700 }}>
                                  {item.positiveCount}
                                  <Box component="span" sx={{ fontWeight: 400 }}> positivo ({Math.round(posPct)}%)</Box>
                                </Typography>
                              )}
                              {item.neutralCount > 0 && (
                                <Typography variant="caption" sx={{ color: SENTIMENT_COLORS[SentimentFilter.neutral], fontWeight: 700 }}>
                                  {item.neutralCount}
                                  <Box component="span" sx={{ fontWeight: 400 }}> neutro ({Math.round(neuPct)}%)</Box>
                                </Typography>
                              )}
                              {item.negativeCount > 0 && (
                                <Typography variant="caption" sx={{ color: SENTIMENT_COLORS[SentimentFilter.negative], fontWeight: 700 }}>
                                  {item.negativeCount}
                                  <Box component="span" sx={{ fontWeight: 400 }}> negativo ({Math.round(negPct)}%)</Box>
                                </Typography>
                              )}
                              {item.negativeCount === 0 && item.neutralCount === 0 && item.positiveCount === 0 && (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {item.total} menções
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    );
                  })}
                </Box>

                {hasMore && (
                  <Button
                    fullWidth
                    size="small"
                    endIcon={<ExpandMoreIcon />}
                    onClick={() => setVisibleCount(v => v + 5)}
                    sx={{
                      mt: 1.5,
                      color: '#79CF62',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'rgba(121,207,98,0.08)' },
                    }}
                  >
                    Ver mais ({canonicals.length - visibleCount} restantes)
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─── Satisfaction Card ────────────────────────────────────────────────────────

const SCORE_LABEL = (avg: number) => {
  if (avg >= 4.5) return { text: 'Excelente', color: '#22c55e' };
  if (avg >= 4.0) return { text: 'Muito bom', color: '#84cc16' };
  if (avg >= 3.5) return { text: 'Bom', color: '#84cc16' };
  if (avg >= 3.0) return { text: 'Regular', color: '#f97316' };
  if (avg >= 2.0) return { text: 'Ruim', color: '#ef4444' };
  return { text: 'Crítico', color: '#dc2626' };
};

const SatisfactionCard: React.FC<{ score: SatisfactionScore | null; loading: boolean }> = ({
  score,
  loading,
}) => {
  if (loading) return <SkeletonCard height={180} />;

  const avg = score?.avgSentiment ?? 0;
  const total = score?.totalComments ?? 0;
  const neg = score?.negativeCount ?? 0;
  const neu = score?.neutralCount ?? 0;
  const pos = score?.positiveCount ?? 0;

  const avgDisplay = avg > 0 ? (Math.floor(avg * 10) / 10).toFixed(1) : '—';
  const pct = avg > 0 ? (avg / 5) * 100 : 0;
  const label = avg > 0 ? SCORE_LABEL(avg) : { text: 'Sem dados', color: '#94a3b8' };
  const satPct = total > 0 ? Math.round((pos / total) * 100) : 0;
  const negPct = total > 0 ? (neg / total) * 100 : 0;
  const neuPct = total > 0 ? (neu / total) * 100 : 0;
  const posPct = total > 0 ? (pos / total) * 100 : 0;

  const R = 54, STROKE = 12;
  const SIZE = (R + STROKE) * 2 + 4;
  const CX = SIZE / 2, CY = SIZE / 2;
  const circ = 2 * Math.PI * R;
  const offset = circ * (1 - pct / 100);

  return (
    <motion.div {...fadeSlideIn}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid rgba(120,105,223,0.10)', animation: `${pulseGlow} 4s ease-in-out infinite` }}>
        <CardContent sx={{ pb: '16px !important' }}>

          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #22c55e, #16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 10px rgba(34,197,94,0.3)' }}>
                <SentimentIcon sx={{ color: '#fff', fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                Satisfação Geral
              </Typography>
            </Box>
            <MuiTooltip title="Total de avaliações que possuem comentário analisado no período" arrow placement="top">
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 400, cursor: 'default', borderBottom: '1px dashed #cbd5e1' }}>
                {total.toLocaleString('pt-BR')} amostras
              </Typography>
            </MuiTooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {/* Circular gauge */}
            <MuiTooltip title={avg > 0 ? `${avgDisplay} de 5` : 'Sem dados'} arrow placement="top">
              <Box sx={{ position: 'relative', flexShrink: 0, cursor: 'default' }}>
                <svg width={SIZE} height={SIZE}>
                  <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f1f5f9" strokeWidth={STROKE} />
                  <motion.circle
                    cx={CX} cy={CY} r={R}
                    fill="none"
                    stroke={label.color}
                    strokeWidth={STROKE}
                    strokeLinecap="round"
                    strokeDasharray={circ}
                    initial={{ strokeDashoffset: circ }}
                    animate={{ strokeDashoffset: avg > 0 ? offset : circ }}
                    transition={{ duration: 0.9, ease: EASE_OUT }}
                    transform={`rotate(-90 ${CX} ${CY})`}
                  />
                </svg>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 800, color: label.color, lineHeight: 1, fontSize: '1.9rem' }}>
                    {avgDisplay}
                  </Typography>
                </Box>
              </Box>
            </MuiTooltip>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Label + satisfaction sentence */}
              <Typography sx={{ fontWeight: 800, color: label.color, lineHeight: 1, mb: 0.5, fontSize: '1.35rem' }}>
                {label.text}
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155', mb: 1.5, fontWeight: 400 }}>
                <Box component="span" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.05rem' }}>
                  {satPct}%
                </Box>
                {' '}dos clientes saíram satisfeitos
              </Typography>

              {/* Split bar */}
              <Box sx={{ height: 10, borderRadius: 99, bgcolor: '#f1f5f9', overflow: 'hidden', display: 'flex', mb: 1.25 }}>
                <motion.div
                  initial={{ width: 0 }} animate={{ width: `${posPct}%` }}
                  transition={{ duration: 0.7, ease: EASE_OUT }}
                  style={{ height: '100%', background: '#22c55e' }}
                />
                <motion.div
                  initial={{ width: 0 }} animate={{ width: `${neuPct}%` }}
                  transition={{ duration: 0.7, ease: EASE_OUT }}
                  style={{ height: '100%', background: '#94a3b8' }}
                />
                <motion.div
                  initial={{ width: 0 }} animate={{ width: `${negPct}%` }}
                  transition={{ duration: 0.7, ease: EASE_OUT }}
                  style={{ height: '100%', background: '#ef4444' }}
                />
              </Box>

              {/* Counts row */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  { key: 'pos', count: pos, p: posPct, color: '#22c55e', label: 'positivas' },
                  { key: 'neu', count: neu, p: neuPct, color: '#94a3b8', label: 'neutras' },
                  { key: 'neg', count: neg, p: negPct, color: '#ef4444', label: 'negativas' },
                ].map(({ key, count, p, color, label: l }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.8rem' }}>
                      <Box component="span" sx={{ fontWeight: 700, color }}>{count}</Box>
                      {' '}{l}{' '}
                      <Box component="span" sx={{ color: '#94a3b8' }}>({Math.round(p)}%)</Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─── Attention Comments Card ──────────────────────────────────────────────────

const SENTIMENT_DOT: Record<number, string> = {
  1: '#ef4444', 2: '#ef4444',
  3: '#94a3b8',
  4: '#22c55e', 5: '#22c55e',
};

const initials = (name: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const avatarColor = (name: string | null) => {
  if (!name) return '#94a3b8';
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#0ea5e9', '#14b8a6', '#84cc16'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const AttentionCommentsCard: React.FC<{
  comments: AttentionComment[];
  loading: boolean;
}> = ({ comments, loading }) => {
  const [visibleCount, setVisibleCount] = useState(5);
  const navigate = useNavigate();

  const { data: stationsData } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const stationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (stationsData ?? []).forEach((s: any) => map.set(s.id, s.name));
    return map;
  }, [stationsData]);

  if (loading) return <SkeletonCard height={400} />;

  const visible = comments.slice(0, visibleCount);
  const hasMore = visibleCount < comments.length;

  return (
    <motion.div {...fadeSlideIn}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid rgba(120,105,223,0.10)', animation: `${pulseGlow} 4s ease-in-out infinite` }}>
        <CardContent sx={{ pb: '16px !important', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #f59e0b, #f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 10px rgba(249,115,22,0.3)' }}>
              <WarningIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', flex: 1, fontSize: '0.95rem' }}>
              Requerem Atenção
            </Typography>
            {comments.length > 0 && (
              <Chip
                label={comments.length}
                size="small"
                sx={{ bgcolor: '#fff7ed', color: '#f97316', fontWeight: 700, height: 24, fontSize: '0.8rem', px: 0.5 }}
              />
            )}
          </Box>

          {comments.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
              <WarningIcon sx={{ fontSize: 40, color: '#e2e8f0' }} />
              <Typography color="text.secondary" variant="body2">
                Nenhum comentário requer atenção no período
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                {visible.map((item, idx) => {
                  const dotColor = item.commentSentiment ? SENTIMENT_DOT[item.commentSentiment] ?? '#94a3b8' : '#94a3b8';
                  const stationName = stationNameMap.get(item.stationId) ?? item.stationId?.slice(0, 8) + '…';
                  const dt = new Date(item.createdAt);
                  const dateStr = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                  const timeStr = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                  const bgColor = avatarColor(item.customerName);

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.04, duration: 0.25, ease: EASE_OUT }}
                    >
                      <Box
                        onClick={() => navigate(`/ratings/${item.id}/show`)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          borderLeft: `3px solid ${dotColor}`,
                          bgcolor: '#fff',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f8fafc', borderColor: '#e2e8f0', borderLeftColor: dotColor },
                          transition: 'background 0.15s',
                        }}
                      >
                        {/* Top row: avatar + customer + station + date/time + link icon */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>

                          {/* Avatar */}
                          <Box
                            sx={{
                              width: 30, height: 30, borderRadius: '50%',
                              bgcolor: bgColor, flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            {item.customerName
                              ? <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                  {initials(item.customerName)}
                                </Typography>
                              : <PersonIcon sx={{ fontSize: 20, color: '#fff' }} />
                            }
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Customer name */}
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', display: 'block', lineHeight: 1.3 }} noWrap>
                              {item.customerName ?? 'Cliente anônimo'}
                            </Typography>
                            {/* Station name */}
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }} noWrap>
                              {stationName}
                            </Typography>
                          </Box>

                          {/* Date + time + link icon */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 0.25 }}>
                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, lineHeight: 1, fontSize: '0.75rem' }}>
                              {dateStr}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', lineHeight: 1 }}>
                              {timeStr}
                            </Typography>
                          </Box>

                          <OpenInNewIcon sx={{ fontSize: 14, color: '#94a3b8', flexShrink: 0, mt: '2px' }} />
                        </Box>

                        {/* Comment text */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#1e293b',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.5,
                            mb: 0.75,
                            fontSize: '0.875rem',
                          }}
                        >
                          {item.comment}
                        </Typography>

                        {/* Bottom row: ratings + category chips */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

                          {/* Station rating */}
                          {item.stationRating != null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <StarIcon sx={{ fontSize: 12, color: '#7869DF' }} />
                              <Typography variant="caption" sx={{ color: '#7869DF', fontWeight: 700, fontSize: '0.75rem' }}>
                                {item.stationRating}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                posto
                              </Typography>
                            </Box>
                          )}

                          {/* Attendant rating */}
                          {item.attendantRating != null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <StarIcon sx={{ fontSize: 12, color: '#79CF62' }} />
                              <Typography variant="caption" sx={{ color: '#79CF62', fontWeight: 700, fontSize: '0.75rem' }}>
                                {item.attendantRating}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                frentista
                              </Typography>
                            </Box>
                          )}

                          {/* Category chips */}
                          {item.commentCategories?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.commentCategories.slice(0, 3).map(cat => (
                                <Chip
                                  key={cat}
                                  label={cat}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#334155' }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </motion.div>
                  );
                })}
              </Box>

              {hasMore && (
                <Button
                  fullWidth
                  size="small"
                  endIcon={<ExpandMoreIcon />}
                  onClick={() => setVisibleCount(v => v + 5)}
                  sx={{
                    mt: 1.5,
                    color: '#79CF62',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(121,207,98,0.08)' },
                  }}
                >
                  Ver mais ({comments.length - visibleCount} restantes)
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────

interface FilterBarProps {
  from: string;
  to: string;
  sentiment: SentimentFilter;
  selectedStationId: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onSentimentChange: (v: SentimentFilter) => void;
  onStationChange: (v: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  from, to, sentiment, selectedStationId,
  onFromChange, onToChange, onSentimentChange, onStationChange,
}) => {
  const { data: stations } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const stationOptions = useMemo(() =>
    (stations ?? []).map((s: any) => ({ id: s.id, label: s.name ?? s.id })),
    [stations]);

  const selectedStationOption = stationOptions.find(o => o.id === selectedStationId) ?? null;

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.05)', mb: 3, bgcolor: 'white', border: '1px solid rgba(120,105,223,0.10)' }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap', overflowX: 'auto' }}>

          <TextField
            label="De"
            type="date"
            variant="outlined"
            size="small"
            value={from}
            onChange={e => onFromChange(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to } }}
            sx={{ minWidth: 155 }}
            fullWidth={false}
          />

          <TextField
            label="Até"
            type="date"
            variant="outlined"
            size="small"
            value={to}
            onChange={e => onToChange(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 155 }}
            fullWidth={false}
          />

          <Autocomplete
            size="small"
            options={stationOptions}
            value={selectedStationOption}
            onChange={(_, val) => onStationChange(val?.id ?? '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText="Nenhum posto encontrado"
            clearText="Limpar"
            renderInput={(params) => <TextField {...params} label="Posto" variant="outlined" />}
            sx={{ minWidth: 330 }}
            fullWidth={false}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 155 }} fullWidth={false}>
            <InputLabel>Sentimento</InputLabel>
            <Select
              value={sentiment}
              label="Sentimento"
              onChange={e => onSentimentChange(e.target.value as SentimentFilter)}
            >
              {SENTIMENT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {opt.value === SentimentFilter.negative && <NegativeIcon sx={{ fontSize: 16, color: SENTIMENT_COLORS[SentimentFilter.negative] }} />}
                    {opt.value === SentimentFilter.positive && <PositiveIcon sx={{ fontSize: 16, color: SENTIMENT_COLORS[SentimentFilter.positive] }} />}
                    {opt.value === SentimentFilter.neutral && <NeutralIcon sx={{ fontSize: 16, color: SENTIMENT_COLORS[SentimentFilter.neutral] }} />}
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const useCanAccessComments = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const perms: string[] = user?.permissions || [];
    return perms.includes(PERMISSIONS.COMMENT_ANALYSIS);
  } catch {
    return false;
  }
};

const CustomerVoice: React.FC = () => {
  const canAccess = useCanAccessComments();
  const { permissions } = usePermissions();
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const chainId = getChainId();
  // Lido aqui (antes do "if (!canAccess) return") pra manter a mesma ordem de
  // hooks nas duas saídas do componente. Só define o estado INICIAL de
  // from/to/filterStationId (atalho "Ver comentários" do card ⭐ Avaliações no
  // Painel 360) — depois disso o usuário continua livre pra trocar os filtros.
  const [searchParams] = useSearchParams();

  useEffect(() => {
    track(EVENTS.DASHBOARD_OPENED, { dashboard: 'customer-voice' });
  }, []);

  if (!canAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Acesso não autorizado</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Você não tem permissão para acessar o Dashboard de Comentários.
        </Typography>
      </Box>
    );
  }

  const [from, setFrom] = useState(() => readInitialRange(searchParams)?.from ?? defaultFrom());
  const [to, setTo] = useState(() => readInitialRange(searchParams)?.to ?? defaultTo());
  const [sentiment, setSentiment] = useState<SentimentFilter>(SentimentFilter.all);
  const [filterStationId, setFilterStationId] = useState(() => readInitialStationId(searchParams));
  const [chart2StationId, setChart2StationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AnalysisData | null>(null);
  const [satisfaction, setSatisfaction] = useState<SatisfactionScore | null>(null);
  const [loadingSat, setLoadingSat] = useState(false);
  const [attention, setAttention] = useState<AttentionComment[]>([]);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [processDays, setProcessDays] = useState(30);
  const [processing, setProcessing] = useState(false);

  // ── Voz do Cliente · IA: novos dados (Seções I, A, B, E, G, H, L) ──
  const [customerRatings, setCustomerRatings] = useState<CustomerRatingsData | null>(null);
  const [loadingCR, setLoadingCR] = useState(false);
  const [pollItems, setPollItems] = useState<PollItemsData | null>(null);
  const [loadingPI, setLoadingPI] = useState(false);
  const [attendants, setAttendants] = useState<AttendantsData | null>(null);
  const [loadingAtd, setLoadingAtd] = useState(false);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [loadingIns, setLoadingIns] = useState(false);
  const [inference, setInference] = useState<InferenceData | null>(null);
  const [loadingInf, setLoadingInf] = useState(false);

  const handleProcessComments = useCallback(async () => {
    if (!chainId) return;
    setProcessing(true);
    try {
      await dataProvider.create('comment-analysis/run', {
        data: { chainId, days: processDays },
      });
      notify(`Análise iniciada (últimos ${processDays} dias). O resultado aparecerá em alguns minutos.`, { type: 'success' });
    } catch (err: any) {
      if (err?.status === 409) {
        notify('Análise já em andamento. Aguarde alguns instantes.', { type: 'warning' });
      } else {
        notify(`Erro ao iniciar análise: ${err?.message ?? 'desconhecido'}`, { type: 'error' });
      }
    } finally {
      setProcessing(false);
    }
  }, [chainId, processDays, dataProvider, notify]);

  const handleFilterStationChange = useCallback((id: string) => {
    setFilterStationId(id);
    if (id) setChart2StationId(id);
  }, []);

  const fetchData = useCallback(async () => {
    if (!chainId) return;
    setLoading(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          sentiment,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as AnalysisData | undefined;
      if (item) {
        setData(item);
        if (!chart2StationId && item.stationBreakdown?.length > 0) {
          setChart2StationId(item.stationBreakdown[0].stationId);
        }
      }
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoading(false);
    }
  }, [chainId, from, to, sentiment, filterStationId, dataProvider]);

  const fetchSatisfaction = useCallback(async () => {
    if (!chainId) return;
    setLoadingSat(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/satisfaction', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as SatisfactionScore | undefined;
      if (item) setSatisfaction(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingSat(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  const fetchAttention = useCallback(async () => {
    if (!chainId) return;
    setLoadingAtt(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/attention', {
        pagination: { page: 1, perPage: 50 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      setAttention((result.data ?? []) as unknown as AttentionComment[]);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingAtt(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  // ── Fetchers novos ──

  const fetchCustomerRatings = useCallback(async () => {
    if (!chainId) return;
    setLoadingCR(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/customer-ratings', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as CustomerRatingsData | undefined;
      if (item) setCustomerRatings(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingCR(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  const fetchPollItems = useCallback(async () => {
    if (!chainId) return;
    setLoadingPI(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/poll-items', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as PollItemsData | undefined;
      if (item) setPollItems(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingPI(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  const fetchAttendants = useCallback(async () => {
    if (!chainId) return;
    setLoadingAtd(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/attendants', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as AttendantsData | undefined;
      if (item) setAttendants(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingAtd(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  const fetchInsights = useCallback(async () => {
    if (!chainId) return;
    setLoadingIns(true);
    try {
      // Insights usa janelas fixas (7/14/30 dias) no backend e ignora from/to
      const result = await dataProvider.getList('chains/chainId/comment-analysis/insights', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as InsightsData | undefined;
      if (item) setInsights(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingIns(false);
    }
  }, [chainId, filterStationId, dataProvider]);

  const fetchInference = useCallback(async () => {
    if (!chainId) return;
    setLoadingInf(true);
    try {
      const result = await dataProvider.getList('chains/chainId/comment-analysis/inference', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          from,
          to,
          ...(filterStationId ? { placeId: filterStationId } : {}),
        },
      });
      const item = result.data?.[0] as unknown as InferenceData | undefined;
      if (item) setInference(item);
    } catch (e) {
      // silently ignore fetch errors
    } finally {
      setLoadingInf(false);
    }
  }, [chainId, from, to, filterStationId, dataProvider]);

  useEffect(() => {
    if (chainId) {
      fetchData();
      fetchSatisfaction();
      fetchAttention();
      fetchCustomerRatings();
      fetchPollItems();
      fetchAttendants();
      fetchInference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, sentiment, filterStationId]);

  // Separado: não depende de período nem sentimento, não precisa refazer quando eles mudam
  useEffect(() => {
    if (chainId) fetchInsights();
  }, [chainId, fetchInsights]);

  const stationBreakdown = data?.stationBreakdown ?? [];

  return (
    <Box sx={{
      p: 2,
      minHeight: 'calc(100vh - 80px)',
      background: 'linear-gradient(145deg, #f0f4ff 0%, #f8fafc 45%, #f5f0ff 100%)',
    }}>
      <Title title="Voz do Cliente · IA" />

      {/* Header */}
      <Box sx={{ mb: 3, pt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Icon with glow */}
        <Box sx={{
          width: 44, height: 44, borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea, #7869DF)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          boxShadow: '0 4px 16px rgba(120,105,223,0.35)',
          animation: `${pulseGlow} 3s ease-in-out infinite`,
        }}>
          <SparkleIcon sx={{ color: '#fff', fontSize: 22, animation: `${sparkleFloat} 2.5s ease-in-out infinite` }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 800, lineHeight: 1.2,
              background: 'linear-gradient(90deg, #1e293b 30%, #7869DF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Voz do Cliente
            </Typography>
            <Box sx={{
              px: 1, py: 0.25, borderRadius: 99,
              background: 'linear-gradient(90deg, #667eea, #7869DF)',
              display: 'flex', alignItems: 'center', gap: 0.4,
            }}>
              <SparkleIcon sx={{ color: '#fff', fontSize: 10 }} />
              <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5 }}>
                IA
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.25 }}>
            Nota do cliente, leitura da IA e tags selecionadas — tudo num lugar só
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <ManualLinkButton slug="voz-do-cliente-ia" label="Manual" />
        </Box>
      </Box>

      {/* Filters */}
      <FilterBar
        from={from}
        to={to}
        sentiment={sentiment}
        selectedStationId={filterStationId}
        onFromChange={setFrom}
        onToChange={setTo}
        onSentimentChange={setSentiment}
        onStationChange={handleFilterStationChange}
      />

      {/* Admin-only: manual comment processing trigger */}
      {isAdmin && (
        <Card sx={{ mb: 3, border: '1px dashed rgba(120,105,223,0.35)', background: 'rgba(120,105,223,0.04)' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto' }}>
              <AnalyticsIcon sx={{ color: '#7869DF' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Processar comentários (admin)
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Dispara o worker para analisar comentários pendentes dos últimos N dias.
                </Typography>
              </Box>
            </Box>
            <TextField
              label="Dias"
              type="number"
              size="small"
              value={processDays}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v)) setProcessDays(Math.max(1, Math.min(365, v)));
              }}
              inputProps={{ min: 1, max: 365 }}
              sx={{ width: 100 }}
              disabled={processing}
            />
            <Button
              variant="contained"
              onClick={handleProcessComments}
              disabled={processing}
              sx={{
                background: 'linear-gradient(135deg, #667eea, #7869DF)',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { background: 'linear-gradient(135deg, #5568d3, #6656c7)' },
              }}
            >
              {processing ? 'Processando…' : 'Processar comentários'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Voz do Cliente · IA: seções novas no topo ────────────────── */}

      {/* [I] Insights automáticos */}
      <InsightsBar bullets={insights?.bullets ?? []} loading={loadingIns} />

      {/* [A] Nota do Cliente × Leitura da IA + [B] Divergência por posto */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 3,
          alignItems: 'start',
        }}
      >
        <DualScoreCard overall={customerRatings?.overall ?? null} loading={loadingCR} />
        <StationDivergenceTable rows={customerRatings?.perStation ?? []} loading={loadingCR} />
      </Box>

      {/* [E] O que elogiam × O que reclamam */}
      <Box sx={{ mb: 3 }}>
        <PollItemsHighlights
          positive={pollItems?.positive ?? []}
          negative={pollItems?.negative ?? []}
          loading={loadingPI}
        />
      </Box>

      {/* [G] Tendência de pollItems */}
      <Box sx={{ mb: 3 }}>
        <PollItemsTrend
          positive={pollItems?.positive ?? []}
          negative={pollItems?.negative ?? []}
          loading={loadingPI}
        />
      </Box>

      {/* [L] Tags da IA — Card condicional (só aparece se enabled + tem inferências) */}
      <Box sx={{ mb: 3 }}>
        <InferenceCard data={inference} loading={loadingInf} />
      </Box>

      {/* [H] Destaques de frentistas */}
      <Box sx={{ mb: 3 }}>
        <AttendantHighlights
          topPraised={attendants?.topPraised ?? []}
          needAttention={attendants?.needAttention ?? []}
          loading={loadingAtd}
        />
      </Box>

      {/* ── Seções existentes (Categorias IA, Detalhe por posto, Atenção) ─── */}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'start' }}>

        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SatisfactionCard score={satisfaction} loading={loadingSat} />
          <CategoryChart data={data?.categorySummary ?? []} loading={loading} />
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <StationCanonicalList
            stationBreakdown={stationBreakdown}
            selectedStationId={chart2StationId}
            onSelectStation={setChart2StationId}
            loading={loading}
          />
          <AttentionCommentsCard comments={attention} loading={loadingAtt} />
        </Box>

      </Box>
    </Box>
  );
};

export default CustomerVoice;
